import * as yup from 'yup';
import { PasswordStrength } from '../types/auth';
import { ALLOWED_PASSWORD_SYMBOLS, PASSWORD_MIN_LENGTH, createPasswordSymbolPattern } from '../../../shared/constants/password';

// Password strength calculation
export const calculatePasswordStrength = (password: string): PasswordStrength => {
  let score = 0;
  const feedback: string[] = [];

  if (password.length >= PASSWORD_MIN_LENGTH) {
    score += 1;
  } else {
    feedback.push(`パスワードは${PASSWORD_MIN_LENGTH}文字以上で入力してください`);
  }

  if (/[a-z]/.test(password)) {
    score += 1;
  } else {
    feedback.push('小文字を含めてください');
  }

  if (/[A-Z]/.test(password)) {
    score += 1;
  } else {
    feedback.push('大文字を含めてください');
  }

  if (/\d/.test(password)) {
    score += 1;
  } else {
    feedback.push('数字を含めてください');
  }

  if (new RegExp(createPasswordSymbolPattern()).test(password)) {
    score += 1;
  } else {
    feedback.push(`記号（${ALLOWED_PASSWORD_SYMBOLS}）を含めてください`);
  }

  let label: PasswordStrength['label'];
  if (score <= 1) {
    label = 'weak';
  } else if (score <= 2) {
    label = 'fair';
  } else if (score <= 3) {
    label = 'good';
  } else {
    label = 'strong';
  }

  return { score, label, feedback };
};

// Email validation regex
const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

// Password validation schema
const passwordSchema = yup
  .string()
  .required('パスワードは必須です')
  .min(PASSWORD_MIN_LENGTH, `パスワードは${PASSWORD_MIN_LENGTH}文字以上で入力してください`)
  .matches(/[a-z]/, 'パスワードには小文字を含めてください')
  .matches(/[A-Z]/, 'パスワードには大文字を含めてください')
  .matches(/\d/, 'パスワードには数字を含めてください')
  .matches(new RegExp(createPasswordSymbolPattern()), `パスワードには記号（${ALLOWED_PASSWORD_SYMBOLS}）を含めてください`);

// Login form validation schema
export const loginSchema = yup.object({
  email: yup
    .string()
    .required('メールアドレスは必須です')
    .matches(emailRegex, '有効なメールアドレスを入力してください'),
  password: yup.string().required('パスワードは必須です'),
  rememberMe: yup.boolean().optional(),
});

// Registration form validation schema
export const registerSchema = yup.object({
  fullName: yup
    .string()
    .required('名前は必須です')
    .min(2, '名前は2文字以上で入力してください')
    .max(50, '名前は50文字以内で入力してください'),
  furigana: yup
    .string()
    .required('ふりがなは必須です')
    .matches(/^[ぁ-んー\s]+$/, 'ふりがなはひらがなで入力してください'),
  university: yup
    .string()
    .required('大学名は必須です')
    .max(100, '大学名は100文字以内で入力してください'),
  faculty: yup
    .string()
    .required('学部・学科は必須です')
    .max(100, '学部・学科は100文字以内で入力してください'),
  phone: yup
    .string()
    .required('電話番号は必須です')
    .matches(/^0\d{9,10}$/, '有効な電話番号を入力してください（例: 09012345678）'),
  birthDate: yup
    .string()
    .required('生年月日は必須です'),
  gender: yup
    .string()
    .required('性別は必須です')
    .oneOf(['male', 'female', 'other'], '性別を選択してください'),
  email: yup
    .string()
    .required('メールアドレスは必須です')
    .matches(emailRegex, '有効なメールアドレスを入力してください')
    .test(
      'no-icloud-domain',
      'iCloudメールアドレス（@icloud.com / @me.com / @mac.com）はメール配信の都合上ご利用いただけません。他のメールアドレスをお使いください。',
      (value) => {
        if (!value) return true;
        const domain = value.split('@')[1]?.toLowerCase() ?? '';
        return !['icloud.com', 'me.com', 'mac.com', 'privaterelay.appleid.com'].includes(domain);
      }
    ),
  password: passwordSchema,
  confirmPassword: yup
    .string()
    .required('パスワード確認は必須です')
    .oneOf([yup.ref('password')], 'パスワードが一致しません'),
  agreeToTerms: yup
    .boolean()
    .required('利用規約への同意は必須です')
    .oneOf([true], '利用規約に同意してください'),
});

// Forgot password form validation schema
export const forgotPasswordSchema = yup.object({
  email: yup
    .string()
    .required('メールアドレスは必須です')
    .matches(emailRegex, '有効なメールアドレスを入力してください'),
});

// Reset password form validation schema
export const resetPasswordSchema = yup.object({
  password: passwordSchema,
  confirmPassword: yup
    .string()
    .required('パスワード確認は必須です')
    .oneOf([yup.ref('password')], 'パスワードが一致しません'),
  token: yup.string().required('トークンが必要です'),
});

// Validation helper functions
export const validateEmail = (email: string): boolean => {
  return emailRegex.test(email);
};

export const validatePasswordStrength = (password: string): boolean => {
  const strength = calculatePasswordStrength(password);
  return strength.score >= 3; // At least 'good' strength required
};

// Real-time validation functions for use with React Hook Form
export const validateEmailField = (email: string): string | undefined => {
  if (!email) return 'メールアドレスは必須です';
  if (!validateEmail(email)) return '有効なメールアドレスを入力してください';
  return undefined;
};

export const validatePasswordField = (password: string): string | undefined => {
  if (!password) return 'パスワードは必須です';
  if (password.length < PASSWORD_MIN_LENGTH) return `パスワードは${PASSWORD_MIN_LENGTH}文字以上で入力してください`;
  return undefined;
};

export const validateConfirmPasswordField = (
  password: string,
  confirmPassword: string
): string | undefined => {
  if (!confirmPassword) return 'パスワード確認は必須です';
  if (password !== confirmPassword) return 'パスワードが一致しません';
  return undefined;
};